import React, { useState } from 'react';
import { LogOut, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import artistIntroLogo from '../assets/artist_intro.svg';

interface NoAccessScreenProps {
  message?: string;
  onSignOut: () => void;
  onRefresh?: () => Promise<void> | void;
}

const NoAccessScreen: React.FC<NoAccessScreenProps> = ({
  message = 'Your access to every artist on this account has been removed. If this is unexpected, ask the artist or management company to invite you again.',
  onSignOut,
  onRefresh,
}) => {
  const [refreshing, setRefreshing] = useState(false);

  const handleRefresh = async () => {
    if (!onRefresh) return;
    setRefreshing(true);
    try {
      await onRefresh();
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div className="h-full flex flex-col bg-white rounded-2xl overflow-hidden">
      <div className="flex flex-col lg:grid lg:grid-cols-5 flex-1 min-h-0 overflow-hidden">
        <div className="lg:col-span-3 flex flex-col flex-1 min-h-0 overflow-y-auto">
          <div className="shrink-0 px-6 sm:px-10 lg:px-16 pt-8 lg:pt-10">
            <img src={artistIntroLogo} alt="Artist Intro" className="h-10 shrink-0" />
          </div>

          <div className="flex-1 flex items-center px-6 sm:px-10 lg:px-16">
            <div className="max-w-2xl w-full">
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3 leading-tight">
                Your access has been <span className="text-red-500">removed</span>
              </h1>

              <p className="text-gray-500 text-sm sm:text-base mb-1 leading-relaxed">
                {message}
              </p>
              <p className="text-gray-400 text-sm mb-8">
                Your account is still active — once someone invites you again, you can sign back in.
              </p>

              <div className="flex flex-col sm:flex-row gap-3">
                <Button variant="primary" type="button" onClick={onSignOut}>
                  <LogOut className="w-4 h-4 mr-2" />
                  Sign Out
                </Button>
                {onRefresh && (
                  <Button variant="outline" type="button" onClick={handleRefresh} disabled={refreshing}>
                    <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                    {refreshing ? 'Checking…' : 'Check Again'}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="hidden lg:flex lg:col-span-2 bg-gray-100 border-l border-gray-100 rounded-tr-2xl rounded-br-2xl" />
      </div>
    </div>
  );
};

export default NoAccessScreen;
